import {
  GetVideoByIdQuery,
  useGetVideoByIdLazyQuery,
  useGetVideoByIdQuery,
} from 'generated/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';

export enum VideoStatus {
  ProcessingStarted = 'PROCESSING_STARTED',
  ProcessingEncodeFinished = 'PROCESSING_ENCODE_FINISHED',
  ProcessingFinished = 'PROCESSING_FINISHED',
  ProcessingError = 'PROCESSING_ERROR',
  UploadStarted = 'UPLOAD_STARTED',
  UploadFinished = 'UPLOAD_FINISHED',
}

export type VideoById = GetVideoByIdQuery['getVideoById'];

const POLLING_INTERVAL = 3000;

export const useVideoStatusModel = ({ creatorId }: { creatorId: number }) => {
  const [fetchVideo] = useGetVideoByIdLazyQuery();
  const [videos, setVideos] = useState(() => new Map<string, VideoById>());
  const [pollingVideos, setPollingVideos] = useState(() => new Set<string>());
  const intervalRefs = useRef(new Map<string, NodeJS.Timeout>());

  const isVideoFinished = useCallback(
    (status: VideoStatus) =>
      status === VideoStatus.ProcessingFinished ||
      status === VideoStatus.ProcessingError,
    [],
  );

  const stopPollingForVideo = useCallback((videoId: string) => {
    const interval = intervalRefs.current.get(videoId);
    if (interval) {
      clearInterval(interval);
      intervalRefs.current.delete(videoId);
    }
    setPollingVideos((prev) => {
      const next = new Set(prev);
      next.delete(videoId);
      return next;
    });
  }, []);

  const fetchAndUpdateVideo = useCallback(
    async (videoId: string, onFinished?: (video: VideoById) => void) => {
      try {
        const { data } = await fetchVideo({
          variables: { creatorId, videoId, videoSourceType: 'mux' },
          fetchPolicy: 'network-only',
        });

        const video = data?.getVideoById;
        if (!video) {
          console.error(`Video not found: ${videoId}`);
          stopPollingForVideo(videoId);
          return null;
        }

        setVideos((prev) => new Map(prev).set(videoId, video));

        if (isVideoFinished(video.status as VideoStatus)) {
          stopPollingForVideo(videoId);
          onFinished?.(video);
        }

        return video;
      } catch (error) {
        console.error(`Error fetching video ${videoId}:`, error);
        stopPollingForVideo(videoId);
        return null;
      }
    },
    [fetchVideo, creatorId, stopPollingForVideo, isVideoFinished],
  );

  const startPollingForVideo = useCallback(
    (videoId: string, onFinished?: (video: VideoById) => void) => {
      if (pollingVideos.has(videoId)) return;

      setPollingVideos((prev) => new Set(prev).add(videoId));
      fetchAndUpdateVideo(videoId, onFinished);

      const interval = setInterval(
        () => fetchAndUpdateVideo(videoId, onFinished),
        POLLING_INTERVAL,
      );

      intervalRefs.current.set(videoId, interval);
    },
    [pollingVideos, fetchAndUpdateVideo],
  );

  const getVideo = useCallback(
    (videoId: string) => videos.get(videoId) ?? null,
    [videos],
  );

  const getCachedVideos = useCallback(
    () => Array.from(videos.values()),
    [videos],
  );

  const stopAllPolling = useCallback(() => {
    intervalRefs.current.forEach(clearInterval);
    intervalRefs.current.clear();
    setPollingVideos(new Set());
  }, []);

  useEffect(() => stopAllPolling, [stopAllPolling]);

  return {
    videos,
    getVideo,
    startPollingForVideo,
    stopPollingForVideo,
    getCachedVideos,
    isPolling: (videoId: string) => pollingVideos.has(videoId),
    pollingCount: pollingVideos.size,
    stopAllPolling,
  };
};

interface UseUploadParams {
  influencerId: number;
  uploadedId: string | null;
  addVideo: (video: VideoById) => void;
  skipProcessing?: boolean;
}

export const useUpload = (p: UseUploadParams) => {
  const [uploadFinished, setUploadFinished] = useState(false);

  const { data, startPolling, stopPolling } = useGetVideoByIdQuery({
    variables: {
      creatorId: p.influencerId,
      videoId: p.uploadedId || '',
      videoSourceType: 'mux',
    },
    fetchPolicy: 'network-only',
    skip: !p.uploadedId,
  });

  const handleVideoProcessing = useCallback(() => {
    const video = data?.getVideoById;
    const status = video?.status;

    if (!status || uploadFinished || !p.uploadedId || !video) return;

    if (status === VideoStatus.ProcessingError) {
      console.error('Video processing failed');
      return;
    }

    const targetStatus = p.skipProcessing
      ? VideoStatus.ProcessingEncodeFinished
      : VideoStatus.ProcessingFinished;

    if (status === targetStatus) {
      p.addVideo(video);
      setUploadFinished(true);
      stopPolling();
    } else {
      startPolling(POLLING_INTERVAL);
    }
  }, [
    data,
    uploadFinished,
    p.uploadedId,
    p.skipProcessing,
    p.addVideo,
    stopPolling,
  ]);

  useEffect(() => {
    handleVideoProcessing();
    return () => stopPolling();
  }, [handleVideoProcessing, stopPolling]);

  return { uploadFinished, startPolling };
};
